import React, {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import {
  type BuildsTierListEntry,
  type BuildsTierListSectionViewModel,
} from '../../../../archon/helpers/getBuildsZonePageViewModels';
import Body from '../Body/Body';
import VerticalContent from '../VerticalContent/VerticalContent';
import ActorIcon from '../ActorIcon/ActorIcon';
import ToggleSelect from '../ToggleSelect/ToggleSelect';
import Heading from '../Heading/Heading';
import ScreenReaderOnly from '@resources/js/components/ScreenReaderOnly/ScreenReaderOnly';
import LazyLoad from 'react-lazyload';
import BuildsNoDataPlaceholder from '../BuildsNoDataPlaceholder/BuildsNoDataPlaceholder';
import { sendEvent } from '@resources/js/helpers/analytics';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import cx from 'classnames';
import EncounterIcon from '../EncounterIcon/EncounterIcon';
import { IconDefaultsContextProvider } from '@resources/js/contexts/useIconDefaults';
import { useElementWidthIsAbove } from '@resources/js/hooks/useElementSize';

type BuildsTierListSectionProps = BuildsTierListSectionViewModel & {
  LinkWrapper: (props: {
    children: React.ReactNode;
    href: string;
  }) => JSX.Element;
  showTierListDescription?: boolean;
  showMetricOptionsAndDescription?: boolean;
  useCompactEntries?: boolean;
  gridTemplateColumns?: string | null;
  showTierListGroupLabel?: boolean;
};

const iconDefaults = {
  icon: { size: 32 },
};

export default function BuildsTierListSection({
  title,
  description,
  label,
  metrics,
  tierLists,
  LinkWrapper,
  showTierListDescription = true,
  showMetricOptionsAndDescription = true,
  useCompactEntries = false,
  gridTemplateColumns = null,
  showTierListGroupLabel = true,
}: BuildsTierListSectionProps): JSX.Element {
  const [metric, setMetric] = useState(metrics[0]);
  const previousMetricsRef = useRef(metrics);
  useEffect(() => {
    if (
      previousMetricsRef.current &&
      metrics.map((m) => m.label).join(',') !==
        previousMetricsRef.current.map((m) => m.label).join(',')
    ) {
      setMetric(metrics[0]);
    }

    previousMetricsRef.current = metrics;
  }, [metrics]);

  let tierList =
    tierLists.find((tierList) => tierList.metric === metric.value) ??
    tierLists[0];

  const tierListHasData = tierList.tiers.length > 0;
  const hasTierListGroups =
    tierListHasData && tierList.tiers[0].entries.length > 1;
  const tierListContainerRef = useRef<HTMLDivElement>(null);
  const tierListContainerIsLarge = useElementWidthIsAbove(
    tierListContainerRef,
    738,
    true,
  );

  const mergedTierList = useMemo(() => {
    return {
      ...tierList,
      tiers: tierList.tiers.map((tier) => ({
        ...tier,
        entries: (tier.entries.length <= 1
          ? tier.entries
          : [tier.entries.flat()]) as BuildsTierListEntry[][],
      })),
    };
  }, [tierList]);

  tierList = useMemo(
    () => (tierListContainerIsLarge ? tierList : mergedTierList),
    [mergedTierList, tierList, tierListContainerIsLarge],
  );
  const hasTierListGroupLabels = useMemo(
    () =>
      tierList.tiers.some((tier) =>
        tier.entries.some((entry) =>
          entry.some((e) => e.tierListGroupLabel !== null),
        ),
      ),
    [tierList.tiers],
  );
  const shouldShowTierListGroupLabel =
    showTierListGroupLabel &&
    hasTierListGroupLabels &&
    tierListContainerIsLarge &&
    hasTierListGroups;

  gridTemplateColumns ??= `auto ${Array(
    tierListHasData ? tierList.tiers[0].entries.length : 1,
  )
    .fill('1fr')
    .join(' ')}`;

  return (
    <VerticalContent className={'builds-tier-list-section'}>
      {showTierListDescription && (
        <Body>
          <Heading level={2}>{title}</Heading>
          <p className={'hide-on-compact'}>{description}</p>
        </Body>
      )}
      {showMetricOptionsAndDescription && metrics.length > 1 && (
        <div>
          <ToggleSelect
            value={metric}
            options={metrics}
            onChange={(newMetric) => {
              sendTierListMetricChangeEvent(newMetric.value);
              setMetric(
                metrics.find((metric) => metric.value === newMetric.value) ??
                  metrics[0],
              );
            }}
          />
        </div>
      )}
      {showMetricOptionsAndDescription && (
        <Body className={'hide-on-compact'}>
          <p className={'builds-tier-list-section__metric-description'}>
            <EnhancedMarkdown markdown={metric.description} wrapped={false} />
          </p>
        </Body>
      )}
      {label && (
        <IconDefaultsContextProvider overrides={iconDefaults}>
          <Heading level={3} className={'builds-tier-list-section__label'}>
            <EnhancedMarkdown markdown={label} wrapped={false} />
          </Heading>
        </IconDefaultsContextProvider>
      )}
      <div
        className={cx({
          'builds-tier-list-section__tiers': true,
        })}
        style={
          gridTemplateColumns && hasTierListGroups
            ? {
                gridTemplateColumns: tierListHasData
                  ? tierListContainerIsLarge
                    ? gridTemplateColumns
                    : 'auto 1fr'
                  : '1fr',
              }
            : {}
        }
        ref={tierListContainerRef}
      >
        {shouldShowTierListGroupLabel ? (
          <>
            <div></div>
            {tierList.tiers[0].entries.map((entry, index) => (
              <div
                key={index}
                className={'builds-tier-list-section__tier-group-header'}
              >
                <Heading level={4} as={'span'}>
                  {entry[0].tierListGroupLabel}
                </Heading>
              </div>
            ))}
          </>
        ) : null}
        {tierListHasData
          ? tierList.tiers.map((tier) =>
              tier.entries.length > 0 ? (
                <Fragment key={tier.tier}>
                  <div
                    className={`builds-tier-list-section__tier-heading ${
                      tierToCssClassName[tier.tier]
                    } ${
                      useCompactEntries
                        ? 'builds-tier-list-section__tier-heading--compact'
                        : ''
                    }`}
                  >
                    <h3 title={`${tier.tier} Tier`}>
                      {tier.tier}
                      <ScreenReaderOnly asSpan> Tier</ScreenReaderOnly>
                    </h3>
                  </div>
                  {(
                    (Array.isArray(tier.entries[0])
                      ? tier.entries
                      : [tier.entries]) as BuildsTierListEntry[][]
                  ).map((entries, index) => (
                    <ul
                      key={index}
                      className={cx({
                        'builds-tier-list-section__specs': true,
                        'builds-tier-list-section__specs--hide-below-md':
                          index > 0,
                      })}
                    >
                      {entries.map((entry) => (
                        <li
                          key={entry.name}
                          className={'builds-tier-list-section__spec'}
                          title={entry.value ? entry.name : undefined}
                        >
                          <LinkWrapper href={entry.url}>
                            {entry.value === null && (
                              <ScreenReaderOnly>{entry.name}</ScreenReaderOnly>
                            )}
                            <div
                              className={`builds-tier-list-section__spec-contents ${
                                entry.cssClassName
                              }-gradient-bg ${
                                entry.cssClassName
                              }-border-color ${
                                useCompactEntries
                                  ? 'builds-tier-list-section__spec-contents--compact'
                                  : ''
                              }`}
                            >
                              <LazyLoad
                                style={{ display: 'inline-block' }}
                                offset={100}
                                placeholder={
                                  <div style={{ width: 47, height: 47 }} />
                                }
                              >
                                {entry.iconType === 'actor' ? (
                                  <ActorIcon
                                    type={entry.icon}
                                    size={47}
                                    className={
                                      'builds-tier-list-section__spec-icon'
                                    }
                                  />
                                ) : (
                                  <EncounterIcon
                                    id={entry.id}
                                    src={entry.icon}
                                    size={47}
                                    className={
                                      'builds-tier-list-section__spec-icon'
                                    }
                                  />
                                )}
                              </LazyLoad>
                              <div
                                className={cx({
                                  'builds-tier-list-section__spec-value-and-label': true,
                                  'builds-tier-list-section__spec-value-and-label--compact':
                                    useCompactEntries,
                                })}
                                style={
                                  tierList.specValueAndLabelPadding
                                    ? {
                                        padding:
                                          tierList.specValueAndLabelPadding,
                                      }
                                    : {}
                                }
                              >
                                <span
                                  className={cx({
                                    'builds-tier-list-section__spec-value': true,
                                    'builds-tier-list-section__spec-value--no-label':
                                      !entry.label,
                                    'builds-tier-list-section__spec-value--no-value':
                                      !entry.value,
                                  })}
                                >
                                  {entry.value ?? entry.name}
                                </span>
                                {entry.label && (
                                  <span
                                    className={
                                      'builds-tier-list-section__spec-label'
                                    }
                                  >
                                    {entry.label}
                                  </span>
                                )}
                              </div>
                            </div>
                          </LinkWrapper>
                        </li>
                      ))}
                    </ul>
                  ))}
                </Fragment>
              ) : null,
            )
          : null}
      </div>
      {!tierListHasData ? <BuildsNoDataPlaceholder /> : null}
    </VerticalContent>
  );
}

const tierToCssClassName: Record<string, string> = {
  S: 'legendary-bg',
  A: 'epic-bg',
  B: 'rare-bg',
  C: 'level-2-bg',
};

function sendTierListMetricChangeEvent(metric: string): void {
  sendEvent({
    analyticsComponentName: 'BuildsZonePage',
    eventName: 'TierListMetricChange',
    extraParameters: {
      filter_1: metric,
    },
  });
}
